import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL
const REQUESTER_BULK_TEMPLATE_URL = `${API_URL}/accounts/requesters/bulk/template`
const REQUESTER_BULK_VALIDATE_URL = `${API_URL}/accounts/requesters/bulk/validate`
const REQUESTER_BULK_CREATE_URL = `${API_URL}/accounts/requesters/bulk/create`

export type RequesterBulkValidationIssue = {
  row: number
  field: string
  errorType: string
  message: string
}

export type RequesterBulkValidationRow = {
  row: number
  data: Record<string, string>
  isValid: boolean
  invalidFields: string[]
}

export type RequesterBulkValidationResult = {
  errorCount: number
  validRowCount: number
  totalRowCount: number
  isValid: boolean
  headers: string[]
  rows: RequesterBulkValidationRow[]
  issues: RequesterBulkValidationIssue[]
}

export type RequesterBulkCreateResult = {
  createdCount: number
  failed: string[]
}

type ApiMessage<T> = {
  status: string
  text: string
  result: T
}

const getValue = (obj: any, camel: string, pascal: string) => obj?.[camel] ?? obj?.[pascal]

const normalizeValidationResult = (result: any): RequesterBulkValidationResult => {
  const errorCount = getValue(result, 'errorCount', 'ErrorCount') ?? 0
  const validRowCount = getValue(result, 'validRowCount', 'ValidRowCount') ?? 0
  const previewRowsRaw =
    getValue(result, 'previewRows', 'PreviewRows') ??
    getValue(result, 'rows', 'Rows') ??
    []

  return {
    errorCount,
    validRowCount,
    totalRowCount: getValue(result, 'totalRowCount', 'TotalRowCount') ?? 0,
    isValid:
      getValue(result, 'isValid', 'IsValid') ?? (errorCount === 0 && validRowCount > 0),
    headers: getValue(result, 'headers', 'Headers') ?? [],
    rows: previewRowsRaw.map((row: any) => ({
      row: getValue(row, 'row', 'Row') ?? 0,
      data: getValue(row, 'data', 'Data') ?? {},
      isValid: getValue(row, 'isValid', 'IsValid') ?? false,
      invalidFields: getValue(row, 'invalidFields', 'InvalidFields') ?? [],
    })),
    issues: (getValue(result, 'issues', 'Issues') ?? []).map((issue: any) => ({
      row: getValue(issue, 'row', 'Row') ?? 0,
      field: getValue(issue, 'field', 'Field') ?? '',
      errorType: getValue(issue, 'errorType', 'ErrorType') ?? '',
      message: getValue(issue, 'message', 'Message') ?? '',
    })),
  }
}

const normalizeCreateResult = (result: any): RequesterBulkCreateResult => ({
  createdCount: getValue(result, 'createdCount', 'CreatedCount') ?? 0,
  failed: getValue(result, 'failed', 'Failed') ?? [],
})

export const downloadRequesterBulkTemplate = async (): Promise<void> => {
  const response = await axios.get(REQUESTER_BULK_TEMPLATE_URL, {responseType: 'blob'})
  const blob = new Blob([response.data], {type: 'text/csv'})
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'requesters_bulk_template.csv'
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const validateRequesterBulkUpload = async (
  file: File
): Promise<RequesterBulkValidationResult> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await axios.post<ApiMessage<any>>(REQUESTER_BULK_VALIDATE_URL, formData, {
    headers: {'Content-Type': 'multipart/form-data'},
    params: {_t: Date.now()},
  })
  return normalizeValidationResult(getValue(response.data, 'result', 'Result'))
}

export const createRequesterBulkUpload = async (
  file: File
): Promise<{validation: RequesterBulkValidationResult | null; create: RequesterBulkCreateResult | null; text: string; status: string}> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await axios.post<ApiMessage<any>>(REQUESTER_BULK_CREATE_URL, formData, {
    headers: {'Content-Type': 'multipart/form-data'},
  })
  return {
    status: getValue(response.data, 'status', 'Status') ?? 'E',
    text: getValue(response.data, 'text', 'Text') ?? '',
    validation: null,
    create: normalizeCreateResult(getValue(response.data, 'result', 'Result')),
  }
}
